import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock } from 'lucide-react'
import { usePresence } from './presence.js'

/**
 * EXPIRATION DE SESSION — anticipée, jamais brutale.
 *
 * VÉRITÉ SERVEUR (§39) : l'échéance vient de `/api/v1/auth/session`
 * (`expires_at` = inactivité glissante, `absolute_expires_at` = borne dure).
 * Le front ne fabrique plus un minuteur depuis « page chargée à X » ; il
 * resynchronise à chaque reprise d'onglet et après chaque prolongation.
 *
 * PROLONGATION (§41) : appel réel à `/auth/session/extend` — le serveur
 * décide, la politique de sécurité reste maîtresse. Une prolongation
 * refusée (borne absolue atteinte) mène à la déconnexion propre.
 *
 * MULTI-ONGLETS (§45) : BroadcastChannel diffuse prolongation et
 * déconnexion — un onglet prolongé calme tous les autres.
 */

const WARN_BEFORE_MS = 5 * 60_000   // le modal s'ouvre 5 minutes avant
const POLL_MS = 60_000              // resynchronisation douce

interface SessionTruth {
  authenticated: boolean
  expires_at: string | null
  absolute_expires_at: string | null
}

/**
 * La session peut-elle encore glisser ?
 *
 * Elle ne le peut que s'il reste de la marge SOUS la borne absolue. Quand les
 * deux échéances coïncident, « Prolonger » réussit sans rien déplacer : le
 * bouton paraît cassé alors qu'il fait exactement ce qu'on lui demande. Mieux
 * vaut ne pas le proposer et dire la vérité — la durée maximale est atteinte.
 */
function peutProlonger(t: { expires_at: string | null; absolute_expires_at: string | null }): boolean {
  if (!t.expires_at || !t.absolute_expires_at) return true
  return new Date(t.expires_at).getTime() < new Date(t.absolute_expires_at).getTime() - 1000
}

type Channel = { postMessage: (m: unknown) => void; close: () => void } | null

function openChannel(onMessage: (m: { type: string }) => void): Channel {
  if (typeof BroadcastChannel === 'undefined') return null
  const ch = new BroadcastChannel('gestion.session')
  ch.onmessage = (ev) => onMessage(ev.data as { type: string })
  return ch as unknown as Channel
}

async function readTruth(): Promise<SessionTruth | null> {
  try {
    const r = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
    if (!r.ok) return null
    return await r.json() as SessionTruth
  } catch { return null }
}

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function SessionExpiryGuard({ authenticated }: { authenticated: boolean }) {
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [prolongeable, setProlongeable] = useState(true)
  const [now, setNow] = useState(() => Date.now())
  const [extending, setExtending] = useState(false)
  const [expired, setExpired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const channelRef = useRef<Channel>(null)

  const sync = useCallback(async () => {
    const t = await readTruth()
    if (!t || !t.authenticated) {
      if (authenticated) setExpired(true)
      return
    }
    setExpiresAt(t.expires_at ? new Date(t.expires_at).getTime() : null)
    setProlongeable(peutProlonger(t))
  }, [authenticated])

  // Vérité serveur : au montage, à chaque retour d'onglet, et périodiquement.
  useEffect(() => {
    if (!authenticated) return
    void sync()
    const onVisible = () => { if (document.visibilityState === 'visible') void sync() }
    document.addEventListener('visibilitychange', onVisible)
    const poll = setInterval(() => { void sync() }, POLL_MS)
    return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(poll) }
  }, [authenticated, sync])

  // Horloge locale (1 s) — uniquement pour l'affichage du décompte.
  useEffect(() => {
    if (!authenticated) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [authenticated])

  // Synchronisation entre onglets.
  useEffect(() => {
    if (!authenticated) return
    channelRef.current = openChannel((m) => {
      if (m.type === 'extended') void sync()
      if (m.type === 'logout') setExpired(true)
    })
    return () => { channelRef.current?.close(); channelRef.current = null }
  }, [authenticated, sync])

  const remaining = expiresAt === null ? null : expiresAt - now
  const warning = authenticated && !expired && remaining !== null
    && remaining <= WARN_BEFORE_MS && remaining > 0

  // Échéance atteinte : transition douce, puis retour au login.
  useEffect(() => {
    if (!authenticated || expired) return
    if (remaining !== null && remaining <= 0) setExpired(true)
  }, [authenticated, expired, remaining])

  useEffect(() => {
    if (!expired) return
    channelRef.current?.postMessage({ type: 'logout' })
    const t = setTimeout(() => {
      document.body.classList.add('lc-session-fadeout')
      setTimeout(() => { window.location.assign('/login?expired=1') }, 260)
    }, 1400)
    return () => clearTimeout(t)
  }, [expired])

  const extend = async () => {
    setExtending(true)
    setError(null)
    try {
      const csrfRes = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
      const csrf = csrfRes.ok ? (await csrfRes.json() as { csrf_token: string | null }).csrf_token : null
      const r = await fetch('/api/v1/auth/session/extend', {
        method: 'POST', credentials: 'same-origin',
        headers: csrf ? { 'x-csrf-token': csrf } : {},
      })
      if (r.status === 401) { setExpired(true); return }
      if (!r.ok) { setError('Prolongation impossible — réessayez.'); return }
      const j = await r.json() as {
        expires_at: string | null; absolute_expires_at: string | null; extended?: boolean
      }
      setExpiresAt(j.expires_at ? new Date(j.expires_at).getTime() : null)
      setProlongeable(peutProlonger(j))
      /* Le serveur dit s'il a VRAIMENT déplacé l'échéance. Prétendre le
         contraire ferait réapparaître le bandeau une seconde plus tard sans
         explication. */
      if (j.extended === false) {
        setError('Durée maximale de session atteinte — reconnectez-vous pour continuer.')
        return
      }
      channelRef.current?.postMessage({ type: 'extended' })
    } catch {
      setError('Prolongation impossible — vérifiez votre connexion.')
    } finally {
      setExtending(false)
    }
  }

  const logout = async () => {
    try {
      const csrfRes = await fetch('/api/v1/auth/session', { credentials: 'same-origin' })
      const csrf = csrfRes.ok ? (await csrfRes.json() as { csrf_token: string | null }).csrf_token : null
      await fetch('/api/v1/auth/logout', {
        method: 'POST', credentials: 'same-origin',
        headers: csrf ? { 'x-csrf-token': csrf } : {},
      })
    } catch { /* on part quand même */ }
    channelRef.current?.postMessage({ type: 'logout' })
    window.location.assign('/login')
  }

  if (expired) return <SessionExpiredOverlay />
  if (!warning) return null
  return (
    <SessionWarningBanner
      remaining={remaining ?? 0}
      extending={extending}
      extendable={prolongeable}
      error={error}
      onExtend={() => void extend()}
      onLogout={() => void logout()}
    />
  )
}

/**
 * BANDEAU DE FIN DE SESSION — une information, pas une modale.
 *
 * L'ancienne version était une carte de la taille d'une boîte de dialogue,
 * plantée en bas à gauche, décompte en très gros. Elle recouvrait une partie
 * de l'application pour dire une chose qui tient sur une ligne.
 *
 * Le bandeau reste horizontal, compact et NON bloquant : la session n'est pas
 * encore finie, rien ne justifie d'interrompre le travail en cours. Il ne
 * verrouille donc ni le défilement, ni le focus.
 */
function SessionWarningBanner({ remaining, extending, extendable, error, onExtend, onLogout }: {
  remaining: number
  extending: boolean
  extendable: boolean
  error: string | null
  onExtend: () => void
  onLogout: () => void
}) {
  const presence = usePresence(true, 200)
  const urgent = remaining <= 60_000
  return createPortal(
    <div className="lc-sessionbar-zone" data-state={presence.state}>
      <div className={`lc-sessionbar${urgent ? ' lc-sessionbar--urgent' : ''}`}
        role="status" aria-live="polite">
        <Clock size={16} aria-hidden="true" className="lc-sessionbar__icon" />
        <p className="lc-sessionbar__text">
          <strong>Session bientôt expirée</strong>
          <span className="lc-sessionbar__sep" aria-hidden="true">·</span>
          <span className="lc-sessionbar__count">{formatCountdown(remaining)}</span>
        </p>
        {error ? <p className="lc-sessionbar__err">{error}</p> : null}
        <div className="lc-sessionbar__actions">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={onLogout}>
            Se déconnecter
          </button>
          {/* Un bouton qui ne peut rien faire ne doit pas être proposé : quand
              la durée maximale est atteinte, seule une reconnexion ouvre une
              nouvelle session. */}
          {extendable ? (
            <button type="button" className="lc-btn lc-btn--primary"
              onClick={onExtend} disabled={extending}>
              {extending ? 'Prolongation…' : 'Prolonger'}
            </button>
          ) : (
            <button type="button" className="lc-btn lc-btn--primary" onClick={onLogout}>
              Se reconnecter
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

function SessionExpiredOverlay() {
  return createPortal(
    <div className="lc-modal-backdrop lc-session-expired" data-state="open" role="presentation">
      <div className="lc-modal lc-session-modal" role="alertdialog" aria-modal="true" aria-live="assertive">
        <h2 className="lc-modal__title">Session expirée</h2>
        <p className="lc-modal__text">Reconnexion en cours…</p>
      </div>
    </div>,
    document.body,
  )
}

/* ─────────────────── interception globale des 401 (§44) ─────────────── */

let expiredNotified = false

/** Une session expirée signalée par l'API produit UNE information, pas une
 *  avalanche de toasts. */
export function notifySessionExpired(): boolean {
  if (expiredNotified) return false
  expiredNotified = true
  try { new BroadcastChannel('gestion.session').postMessage({ type: 'logout' }) } catch { /* ignoré */ }
  document.body.classList.add('lc-session-fadeout')
  setTimeout(() => window.location.assign('/login?expired=1'), 260)
  return true
}
